/**NIRSALCarbonStrategy — Pathway recommendations & quick wins */
import React, { useState } from "react";
import { useNIRSAL } from "@/context/NIRSALContext";
import { PageHeader, SectionCard } from "@/components/ds";

const fmtN = (v, u="") => { if (!v) return "—"; const n=Number(v); return n>=1000 ? `${(n/1000).toFixed(0)}K ${u}`.trim() : `${n} ${u}`.trim(); };

const STRATEGY_MAP = {
  article6_4: { label: "Article 6.4 Candidate", color: "#1e3a5f", bg: "#eff6ff", why: ["High-integrity pathway", "Bilateral ITMO potential", "NDC-aligned sector"] },
  vcm:        { label: "VCM Recommended", color: "#059669", bg: "#ecfdf5", why: ["AMS methodology aligned", "18–24 month pipeline", "VCS/GS eligible"] },
  plastic:    { label: "Plastic Credit Candidate", color: "#7c3aed", bg: "#f5f3ff", why: ["Verra Plastic Standard", "Agri-waste sector relevance", "Community co-benefit"] },
  internal:   { label: "Internal / CSA Only", color: "#64748b", bg: "#f8fafc", why: ["Data quality insufficient", "Consider after evidence upgrade"] },
};

export default function NIRSALCarbonStrategy() {
  const { vbCache, selectedPeriod, loading } = useNIRSAL();
  const [view, setView] = useState("all");
  const vb   = vbCache[selectedPeriod] || {};
  const opps = vb.opportunities || [];

  const filtered = view === "all" ? opps :
    view === "quickwin" ? opps.filter(o => o.flags?.includes("High Impact") || o.flags?.includes("CRG Leverage")) :
    view === "art6"     ? opps.filter(o => o.art6_eligible) :
    view === "plastic"  ? opps.filter(o => o.is_plastic) :
    opps;

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading strategy…</div>;

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-6">
      <PageHeader label="CARBON STRATEGY" title="" />
      <p className="text-xs text-slate-400 -mt-4">Pathway recommendation per cluster · Quick wins · Scalable CPAs · Art.6.4 candidates</p>

      <div className="flex gap-2 flex-wrap">
        {[["all","All Clusters"],["quickwin","Quick Wins"],["art6","Art.6.4"],["plastic","Plastic"]].map(([v,l]) => (
          <button key={v} onClick={() => setView(v)}
            className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${view===v ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white text-slate-500 border-slate-200"}`}>{l}</button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map((o, i) => {
          const strategy = STRATEGY_MAP[o.pathway] || STRATEGY_MAP.internal;
          return (
            <div key={o.opp_id} className="bg-white rounded-xl border border-slate-100 shadow-sm p-5" style={{ borderLeft: `3px solid ${strategy.color}` }} data-testid={`strategy-row-${i}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-black text-slate-800">#{i+1} {o.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: strategy.bg, color: strategy.color }}>{strategy.label}</span>
                    {(o.flags || []).map(f => (
                      <span key={f} className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{f}</span>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500">{o.state} · {o.commodity} · {(o.technology||"").replace(/_/g," ")}</p>
                </div>
                <div className="text-right ml-4">
                  <p className="text-sm font-black text-emerald-700">{fmtN(o.reduction_tco2e_yr,"tCO₂e/yr")}</p>
                  <p className="text-[10px] text-slate-400">Score: {(o.composite_score||0).toFixed(0)}/100</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-50">
                <p className="text-[10px] font-bold text-slate-500 mb-1">WHY SELECTED</p>
                <div className="flex gap-2 flex-wrap">
                  {strategy.why.map(w => <span key={w} className="text-[10px] px-2 py-0.5 bg-slate-50 rounded text-slate-500 border border-slate-100">{w}</span>)}
                  {o.crg_coverage_pct > 0 && <span className="text-[10px] px-2 py-0.5 bg-blue-50 rounded text-blue-600 border border-blue-100">CRG {o.crg_coverage_pct}% coverage</span>}
                  {o.evidence_level === "high" && <span className="text-[10px] px-2 py-0.5 bg-emerald-50 rounded text-emerald-600 border border-emerald-100">Evidence Ready</span>}
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="text-xs text-slate-400 p-6 text-center bg-white rounded-xl border">No clusters match this filter</div>}
      </div>
    </div>
  );
}
